import React, { useRef } from 'react';
import "./style.scss";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

interface TextAreaProps {
    label?: string;
    direction?: "row" | "column";
    placeholder?: string;
    value: string;
    error?: { isError: boolean; message: string };
    resizeX?: boolean;
    resizeY?: boolean;
    id?: string;
    wrapperClassName?: string;
    className?: string;
    wrapperStyle?: React.CSSProperties;
    style?: React.CSSProperties;
    disabled?: boolean;
    callback: (value: string) => void;
}

/**
 * This component is a text area which displays and maintains every line as a bullet point.
 * It returns the value with the bullet points, and ensures every new line starts with a bullet.
 */
const ChangelogTextArea = ({
    label,
    direction,
    placeholder,
    value,
    resizeX,
    resizeY,
    id,
    wrapperClassName,
    className,
    wrapperStyle,
    style,
    disabled,
    callback
}: TextAreaProps) => {
    const { t } = useTranslation();
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const wrapperClass = classNames("text-area-wrapper", {
        [`direction-${direction}`]: direction,
        [`${wrapperClassName}`]: wrapperClassName
    });

    const textAreaClass = classNames("oliver-text-area", {
        ["resize-x"]: resizeX,
        ["resize-y"]: resizeY,
        [`${className}`]: className
    });

    const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
        let inputValue = e.currentTarget.value;
        
        // Ensure each line starts with a bullet point
        inputValue = inputValue.split('\n').map(line => {
            return line.trim().startsWith('•') ? line : `• ${line.trim()}`;
        }).join('\n');

        callback(inputValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const cursorPosition = e.currentTarget.selectionStart;
            const currentValue = e.currentTarget.value;
            const newValue = currentValue.slice(0, cursorPosition) + '\n• ' + currentValue.slice(cursorPosition);
            e.currentTarget.value = newValue;
            callback(newValue);

            // Set cursor position after the new bullet point
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.setSelectionRange(cursorPosition + 3, cursorPosition + 3);
                }
            }, 0);
        } else if (e.key === 'Backspace' && textareaRef.current) {
            const cursorPosition = textareaRef.current.selectionStart;
            const currentValue = textareaRef.current.value;
            const textBeforeCursor = currentValue.slice(0, cursorPosition);

            // Check if we're at the start of a line (right after a bullet point)
            if (textBeforeCursor.endsWith('\n• ') || (cursorPosition === 2 && textBeforeCursor === '• ')) {
                e.preventDefault();
                const newValue = textBeforeCursor.slice(0, -2) + currentValue.slice(cursorPosition);
                textareaRef.current.value = newValue;
                callback(newValue);

                // Set cursor position
                setTimeout(() => {
                    if (textareaRef.current) {
                        textareaRef.current.setSelectionRange(cursorPosition - 2, cursorPosition - 2);
                    }
                }, 0);
            }
        }
    };

    return (
        <label className={wrapperClass} style={wrapperStyle}>
            {t(`generalComponents.textArea.${label}`)}
            <textarea
                ref={textareaRef}
                id={id}
                className={textAreaClass}
                style={style}
                disabled={disabled}
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                placeholder={placeholder && t("generalComponents.textArea." + placeholder)}
                value={value}
            />
        </label>
    );
};

export default ChangelogTextArea;